import React, { useState, useEffect, useCallback } from 'react'
import { connect } from 'react-redux'
import { useHistory, useRouteMatch } from 'react-router-dom'
import axios from 'axios'
import { Editor } from 'react-draft-wysiwyg'
import { EditorState, convertToRaw, ContentState } from 'draft-js'
import draftToHtml from 'draftjs-to-html'
import htmlToDraft from 'html-to-draftjs'
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css'
import {
  Box, Typography, TextField, Button, MenuItem, FormControl, InputLabel,
  Select, Chip, Paper, Divider, CircularProgress, Snackbar
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import Alert from '@material-ui/lab/Alert'
import SaveIcon from '@material-ui/icons/Save'
import PublishIcon from '@material-ui/icons/Publish'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import VisibilityIcon from '@material-ui/icons/Visibility'

const TRACKS = [
  { value: 'subcontractor_setup', label: 'Subcontractor Setup' },
  { value: 'sub_portal', label: 'Subcontractor Portal' },
  { value: 'field_ops', label: 'Field Operations' },
  { value: 'herbicide_queue', label: 'Herbicide Queue' },
  { value: 'site_measurement', label: 'Site Measurement' }
]

const AUDIENCES = [
  { value: 'office', label: 'Office (all employees)' },
  { value: 'admin_only', label: 'Admin Only (hidden)' },
  { value: 'sub_admin', label: 'Sub Admin' },
  { value: 'applicator', label: 'Applicator' },
  { value: 'estimator', label: 'Estimator' }
]

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    maxWidth: 1000,
    margin: '0 auto'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3)
  },
  formRow: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  editorWrapper: {
    border: '1px solid #ccc',
    borderRadius: 4,
    padding: theme.spacing(1),
    minHeight: 300,
    marginBottom: theme.spacing(2),
    backgroundColor: '#fff'
  },
  editorToolbar: {
    borderBottom: '1px solid #e0e0e0',
    marginBottom: 8
  },
  metaSection: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  audienceChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
    marginTop: theme.spacing(1)
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'flex-end',
    marginTop: theme.spacing(2)
  }
}))

const ArticleEditor = ({ articleId, ...props }) => {
  const classes = useStyles()
  const history = useHistory()
  const { url } = useRouteMatch()
  const kbRoot = url.replace(/\/(create|edit\/.*)$/, '')

  const [title, setTitle] = useState('')
  const [summary, setSummary] = useState('')
  const [track, setTrack] = useState('subcontractor_setup')
  const [trackOrder, setTrackOrder] = useState(0)
  const [audience, setAudience] = useState(['office'])
  const [status, setStatus] = useState('draft')
  const [coverImage, setCoverImage] = useState('')
  const [videoUrl, setVideoUrl] = useState('')
  const [tags, setTags] = useState('')
  const [editorState, setEditorState] = useState(EditorState.createEmpty())
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })
  const [showPreview, setShowPreview] = useState(false)

  // Load existing article if editing
  useEffect(() => {
    if (articleId) {
      loadArticle()
    }
  }, [articleId])

  const loadArticle = async () => {
    try {
      setLoading(true)
      // Fetch by ID — we need full body, so use a search that returns the full doc
      const { data: articles } = await axios.get('/api/knowledgebase/articles')
      const article = articles.find(a => a._id === articleId)
      if (!article) {
        // Try fetching by slug to get the body
        setSnackbar({ open: true, message: 'Article not found', severity: 'error' })
        return
      }
      // Need the full body, fetch by slug
      const { data: full } = await axios.get(`/api/knowledgebase/articles/${article.slug}`)
      setTitle(full.title || '')
      setSummary(full.summary || '')
      setTrack(full.track || 'subcontractor_setup')
      setTrackOrder(full.trackOrder || 0)
      setAudience(full.audience || ['office'])
      setStatus(full.status || 'draft')
      setCoverImage(full.coverImage || '')
      setVideoUrl(full.videoUrl || '')
      setTags((full.tags || []).join(', '))

      if (full.body) {
        const blocksFromHtml = htmlToDraft(full.body)
        if (blocksFromHtml) {
          const contentState = ContentState.createFromBlockArray(blocksFromHtml.contentBlocks)
          setEditorState(EditorState.createWithContent(contentState))
        }
      }
    } catch (e) {
      console.error('Error loading article:', e)
      setSnackbar({ open: true, message: 'Error loading article', severity: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleAudienceToggle = (value) => {
    setAudience(prev =>
      prev.includes(value)
        ? prev.filter(v => v !== value)
        : [...prev, value]
    )
  }

  const uploadImageCallback = useCallback(async (file) => {
    try {
      // Get presigned URL
      const { data } = await axios.post('/api/knowledgebase/image-upload-url', {
        fileName: `${Date.now()}_${file.name}`,
        contentType: file.type
      })
      // Upload to S3
      await axios.put(data.uploadUrl, file, {
        headers: { 'Content-Type': file.type }
      })
      return { data: { link: data.objectUrl } }
    } catch (e) {
      console.error('Image upload failed:', e)
      return { data: { link: '' } }
    }
  }, [])

  const getBody = () => {
    return draftToHtml(convertToRaw(editorState.getCurrentContent()))
  }

  const handleSave = async (publishStatus) => {
    if (!title.trim()) {
      setSnackbar({ open: true, message: 'Title is required', severity: 'error' })
      return
    }
    try {
      setSaving(true)
      const payload = {
        title: title.trim(),
        body: getBody(),
        summary: summary.trim(),
        track,
        trackOrder: Number(trackOrder) || 0,
        audience,
        status: publishStatus || status,
        coverImage: coverImage.trim() || undefined,
        videoUrl: videoUrl.trim() || undefined,
        tags: tags.split(',').map(t => t.trim()).filter(Boolean)
      }

      if (articleId) {
        await axios.put(`/api/knowledgebase/articles/${articleId}`, payload)
        setSnackbar({ open: true, message: 'Article updated', severity: 'success' })
      } else {
        const { data } = await axios.post('/api/knowledgebase/articles', payload)
        setSnackbar({ open: true, message: 'Article created', severity: 'success' })
        // Redirect to edit mode with the new ID
        setTimeout(() => history.replace(`${kbRoot}/edit/${data._id}`), 500)
      }
    } catch (e) {
      console.error('Save error:', e)
      setSnackbar({ open: true, message: e.response?.data?.error || 'Error saving article', severity: 'error' })
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => history.push(kbRoot)}>
            Back
          </Button>
          <Typography variant="h5" style={{ fontWeight: 600 }}>
            {articleId ? 'Edit Article' : 'New Article'}
          </Typography>
        </Box>
        <Box className={classes.actions}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<VisibilityIcon />}
            onClick={() => setShowPreview(!showPreview)}
          >
            {showPreview ? 'Editor' : 'Preview'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<SaveIcon />}
            onClick={() => handleSave('draft')}
            disabled={saving}
          >
            Save Draft
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<PublishIcon />}
            onClick={() => handleSave('published')}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Publish'}
          </Button>
        </Box>
      </Box>

      {/* Title + Summary */}
      <TextField
        fullWidth
        label="Title"
        variant="outlined"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        style={{ marginBottom: 16 }}
        required
      />
      <TextField
        fullWidth
        label="Summary (shown on cards)"
        variant="outlined"
        value={summary}
        onChange={(e) => setSummary(e.target.value)}
        multiline
        rows={2}
        style={{ marginBottom: 16 }}
      />

      {/* Metadata */}
      <Paper className={classes.metaSection} variant="outlined">
        <Typography variant="subtitle2" gutterBottom>Article Settings</Typography>
        <Box className={classes.formRow}>
          <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
            <InputLabel>Track</InputLabel>
            <Select value={track} onChange={(e) => setTrack(e.target.value)} label="Track">
              {TRACKS.map(t => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Order"
            type="number"
            variant="outlined"
            size="small"
            value={trackOrder}
            onChange={(e) => setTrackOrder(e.target.value)}
            style={{ width: 100 }}
          />
          <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select value={status} onChange={(e) => setStatus(e.target.value)} label="Status">
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="published">Published</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Typography variant="body2" style={{ marginBottom: 4 }}>Audience (who can see this)</Typography>
        <Box className={classes.audienceChips}>
          {AUDIENCES.map(a => (
            <Chip
              key={a.value}
              label={a.label}
              clickable
              color={audience.includes(a.value) ? 'primary' : 'default'}
              onClick={() => handleAudienceToggle(a.value)}
            />
          ))}
        </Box>

        <Box className={classes.formRow} style={{ marginTop: 16 }}>
          <TextField
            fullWidth
            label="Cover Image URL"
            variant="outlined"
            size="small"
            value={coverImage}
            onChange={(e) => setCoverImage(e.target.value)}
            placeholder="https://..."
          />
          <TextField
            fullWidth
            label="Video URL (YouTube/Loom)"
            variant="outlined"
            size="small"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            placeholder="https://www.loom.com/share/..."
          />
        </Box>
        <TextField
          fullWidth
          label="Tags (comma-separated)"
          variant="outlined"
          size="small"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          placeholder="subcontractor, setup, portal"
        />
      </Paper>

      <Divider style={{ margin: '16px 0' }} />

      {/* Editor / Preview */}
      {showPreview ? (
        <Paper variant="outlined" style={{ padding: 24, minHeight: 300 }}>
          <Typography variant="h5" gutterBottom>{title || 'Untitled'}</Typography>
          <Box dangerouslySetInnerHTML={{ __html: getBody() }} />
        </Paper>
      ) : (
        <Box>
          <Typography variant="subtitle2" gutterBottom>Article Body</Typography>
          <Box className={classes.editorWrapper}>
            <Editor
              editorState={editorState}
              onEditorStateChange={setEditorState}
              toolbar={{
                options: ['inline', 'blockType', 'fontSize', 'list', 'textAlign', 'link', 'image', 'history'],
                inline: { options: ['bold', 'italic', 'underline', 'strikethrough'] },
                image: {
                  uploadCallback: uploadImageCallback,
                  previewImage: true,
                  alt: { present: true, mandatory: false },
                  inputAccept: 'image/gif,image/jpeg,image/jpg,image/png,image/svg'
                }
              }}
              editorClassName="kb-editor-content"
              wrapperClassName="kb-editor-wrapper"
              toolbarClassName={classes.editorToolbar}
            />
          </Box>
        </Box>
      )}

      {/* Bottom actions */}
      <Box className={classes.actions} style={{ marginTop: 24 }}>
        <Button onClick={() => history.push(kbRoot)}>Cancel</Button>
        <Button
          variant="outlined"
          startIcon={<SaveIcon />}
          onClick={() => handleSave('draft')}
          disabled={saving}
        >
          Save Draft
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<PublishIcon />}
          onClick={() => handleSave('published')}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Publish'}
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

const mapStateToProps = (state) => ({
  user: state.user,
  authenticated: state.authenticated
})

export default connect(mapStateToProps)(ArticleEditor)
